import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, FormControl, Grid, Grow, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField } from "@mui/material";
import { commonSort, getTitle } from "../../common";
import AddButton from "../../components/AddButton/AddButton";
import SearchBar from "../../components/SearchBar/SearchBar";
import TableButtons from "../../components/Button/TableButtons";
import LoadingBackdrop from "../../components/LoadingBackdrop/LoadingBackdrop";
import Modal from "../../components/Modal/Modal";
import { StyledTableDiv } from "../../components/StyledComponents/StyledComponents";
import { getBreakdownEffects, addBreakdownEffect, updateBreakdownEffect, deleteBreakdownEffect } from "../../store/actions/BreakdownEffectActions";

export default function BreakdownEffect() {
    const { t } = useTranslation("translation");

    const dispatch = useDispatch();
    const [initialRows, setInitialRows] = useState([]);
    let [rows, setRows] = useState([]);
    const [pageLoaded, setPageLoaded] = useState(false);

    const { BreakdownEffect } = useSelector((BreakdownEffect) => BreakdownEffect);

    useEffect(() => {
        loadHandler();
    }, []);

    useEffect(() => {
        if (BreakdownEffect?.breakdownEffects?.resultObject) {
            setInitialRows(BreakdownEffect.breakdownEffects.resultObject);
            setRows(BreakdownEffect.breakdownEffects.resultObject);
            BreakdownEffect.breakdownEffects = {};
            setPageLoaded(true);
        }
        if (BreakdownEffect?.isTaskUpdated) {
            setEditableRow(false);
            setDisableButton(false);
            loadHandler();
        }
    }, [BreakdownEffect]);

    const loadHandler = () => {
        dispatch(getBreakdownEffects());
    };

    // Update and Add in one function
    const updateAddHandler = (row) => {
        // Update
        if (row.id !== "") {
            let data = {
                id: row.id,
                name: row.name,
                isStopped: isStopped,
                priority: priority,
                status: 1,
            };
            dispatch(updateBreakdownEffect(data));
        }
        // Add
        else {
            let data = {
                name: row.name,
                isStopped: isStopped,
                priority: priority ?? 0,
            };
            dispatch(addBreakdownEffect(data));
        }
    };

    // Delete row
    const deleteHandler = (row) => {
        toggleModal();
        let data = {
            id: row.id,
            name: row.name,
            isStopped: row.isStopped,
            priority: row.priority,
        };
        dispatch(deleteBreakdownEffect(data));
    };

    // Add blank row for new plant addition
    const addBlankRowHandler = () => {
        setCurrentPage(0);
        setDisableButton(true);
        let addedBlankRow = [
            {
                id: "",
                name: "",
                breakdownEffectLocalizationName: "",
                isStopped: "",
                priority: "",
            },
        ];
        let newRows = addedBlankRow.concat(rows);
        setEditableRow(addedBlankRow[0].id);
        setEditable(true);
        setRows(newRows);

        setError({
            breakdownEffectNameText: t("EmptyCheck"),
            priorityText: t("PriorityMustBeGreater"),
        });
    };

    // Filter table
    const [filterText, setFilterText] = useState("");
    const filterData = (value) => {
        setFilterText(value);
        const newFilteredText = value.toLowerCase();
        let filteredItems = cloneObject(initialRows).filter(
            (item) =>
                item.id?.toString().includes(newFilteredText) || item.name?.toLowerCase().includes(newFilteredText) || item.breakdownEffectLocalizationName?.toLowerCase().includes(newFilteredText)
        );
        setRows(filteredItems);
        setDisableButton(false);
    };

    useEffect(() => {
        filterData(filterText);
    }, [initialRows]);

    // isStopped
    let [isStopped, setIsStopped] = useState(true);
    const isStoppedHandler = (event) => {
        //get boolean value for is stopped
        setIsStopped(event.target.value === "true");
    };

    // priority
    let [priority, setPriority] = useState("");
    const PriorityHandler = (event) => {
        setPriority(Number(event.target.value));
    };

    // Sort function
    let [sortOrder, setSortOrder] = useState();
    let [sortColumn, setSortColumn] = useState();
    const handleSort = (row, column) => {
        const sortedList = commonSort(row, column);
        setSortOrder(sortedList[0]);
        setSortColumn(sortedList[1]);
        setInitialRows(sortedList[2]);
    };

    // Pagination
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const handleChangePage = (_event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setCurrentPage(0);
    };

    // Set edited rows
    const setRowHandler = (value, obj, updatedRow) => {
        const index = rows.indexOf(updatedRow);
        if (updatedRow.id !== "") {
            const row = rows[index];
            row[obj] = value;
            rows[index] = row;
            setRows(rows);
        } else {
            const row = rows[index];
            row[obj] = value;
            setRows(rows);
        }

        // Validation
        if (value.length > 0) {
            if (obj === "name") {
                setError({ ...error, breakdownEffectNameText: "" });
            }
        } else {
            if (obj === "name") {
                setError({ ...error, breakdownEffectNameText: t("EmptyCheck") });
            }
        }
    };

    const cloneObject = (obj) => {
        return JSON.parse(JSON.stringify(obj));
    };

    // Edit rows
    const [editable, setEditable] = useState(false);
    const [editableRow, setEditableRow] = useState("");
    const enableEditHandler = (row) => {
        setIsStopped(row.isStopped);
        setPriority(row.priority);

        if (disableButton || editable) {
            filterData(filterText);
        }
        setEditableRow(row.id);
        setEditable(true);

        setError({
            breakdownEffectNameText: "",
            priorityText: "",
        });
    };

    // Enable disable add button
    const [disableButton, setDisableButton] = useState(false);
    const disableEditHandler = () => {
        filterData(filterText);
        setEditable(false);
        setDisableButton(false);

        setError({
            ...error,
            breakdownEffectNameText: t("EmptyCheck"),
            priorityText: t("PriorityMustBeGreater"),
        });
    };

    // Delete Modal
    const [isModalOpen, setModalOpen] = useState(false);
    const [deletedRow, setDeletedRow] = useState([]);
    const deleteButtonHandler = (row) => {
        setDeletedRow(row);
        toggleModal();
    };

    const toggleModal = () => {
        setModalOpen((prevState) => !prevState);
    };

    const renderTableCell = (rowItems, isSortingAction = true) => {
        const directionType = sortOrder === 1 ? "asc" : "desc";
        return rowItems.map((item, index) => {
            if (item.id === "Actions") {
                return (
                    <TableCell id={`be_rowItems_TableCell_Actions_${index}_${item.id}`} align="center">
                        {t("Actions")}
                    </TableCell>
                );
            } else if (item.id === "") {
                return <TableCell id={`be_rowItems_TableCell_${index}_${item.id}`} align="center"></TableCell>;
            } else {
                return isSortingAction ? (
                    <TableCell id={`be_rowItems_TableCell_${index}_${item.id}`} align="center">
                        <TableSortLabel
                            id={`be_rowItems_TableSortLabel_${index}_${item.id}`}
                            active={sortColumn === item.id}
                            direction={directionType}
                            onClick={() => handleSort(cloneObject(initialRows), item.id)}
                            style={{ paddingLeft: 25 }}
                        >
                            {item.label}
                        </TableSortLabel>
                    </TableCell>
                ) : (
                    <TableCell align="center">{item.label}</TableCell>
                );
            }
        });
    };

    // Validation
    const [error, setError] = useState({
        breakdownEffectNameText: t("EmptyCheck"),
        priorityText: t("PriorityMustBeGreater"),
    });
    const [isAddDisable, setIsAddDisable] = useState(true);

    useEffect(() => {
        const newPriorityText = priority ? "" : t("PriorityMustBeGreater");
        setError({
            ...error,
            priorityText: newPriorityText,
        });
    }, [priority]);

    useEffect(() => {
        if (error.breakdownEffectNameText === "" && error.priorityText === "") {
            setIsAddDisable(false);
        } else {
            setIsAddDisable(true);
        }
    }, [error]);

    return (
        <StyledTableDiv>
            {getTitle(t("BreakdownEffect") + " " + t("List"))}
            <AddButton onClick={() => addBlankRowHandler()} disabled={disableButton} />
            {pageLoaded === true ? (
                <Grid container spacing={4} alignItems="center" justifyContent="center" sx={{ marginBottom: 4 }}>
                    <Grid item xs={12} sm={12} md={4}>
                        <SearchBar value={filterText} onChange={(newValue) => setFilterText(newValue)} onRequestSearch={(value) => filterData(value)} />
                    </Grid>
                </Grid>
            ) : null}
            <Box m={2} />
            {rows.length > 0 ? (
                <Grow in={true} style={{ transformOrigin: "0 0 0" }} {...{ timeout: 1000 }}>
                    <TableContainer id="be_TableContainer" component={Paper}>
                        <Table id="be_Table" aria-label="simple table">
                            <TableHead id="be_TableHead">
                                <TableRow id="be_TableRow">
                                    {renderTableCell([
                                        { id: "id", label: t("ID") },
                                        { id: "name", label: t("BreakdownEffectName") },
                                        { id: "breakdownEffectLocalizationName", label: t("BreakdownEffectNameLocalized") },
                                        { id: "isStopped", label: t("Stoppage") },
                                        { id: "priority", label: t("Priority") },
                                        { id: "Actions", label: t("Actions") },
                                    ])}
                                </TableRow>
                            </TableHead>
                            <TableBody id="be_TableBody">
                                {rows.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage).map((row, index) => (
                                    <TableRow id={`be_TableRow_${index}`} key={row.id}>
                                        <TableCell id={`be_TableCell_${index}`} align="center">
                                            {row.id}
                                        </TableCell>
                                        <TableCell id={`be_TableCell_1_${index}`} align="center">
                                            {editable && editableRow === row.id ? (
                                                <TextField
                                                    id={`be_TableCell_1_TextField_${index}`}
                                                    onInput={(e) => setRowHandler(e.target.value, "name", row)}
                                                    defaultValue={row.name}
                                                    variant="outlined"
                                                    error={error.breakdownEffectNameText.length > 0}
                                                    helperText={error.breakdownEffectNameText}
                                                />
                                            ) : (
                                                row.name
                                            )}
                                        </TableCell>
                                        <TableCell id={`be_TableCell_2_${index}`} align="center">
                                            {row.breakdownEffectLocalizationName}
                                        </TableCell>
                                        <TableCell id={`be_TableCell_3_${index}`} align="center">
                                            {editable && editableRow === row.id ? (
                                                <FormControl id={`be_TableCell_3_FormControl_${index}`} variant="outlined">
                                                    <Select id={`be_TableCell_3_Select_${index}`} native outlined onChange={isStoppedHandler} defaultValue={row.isStopped}>
                                                        <option value={true}>{t("Yes")}</option>
                                                        <option value={false}>{t("No")}</option>
                                                    </Select>
                                                </FormControl>
                                            ) : row.isStopped === true ? (
                                                t("Yes")
                                            ) : (
                                                t("No")
                                            )}
                                        </TableCell>
                                        <TableCell id={`be_TableCell_4_${index}`} align="center">
                                            {editable && editableRow === row.id ? (
                                                <TextField
                                                    id={`be_TableCell_4_TextField_${index}`}
                                                    type="number"
                                                    onChange={PriorityHandler}
                                                    defaultValue={row.priority}
                                                    variant="outlined"
                                                    inputProps={{ max: "99", min: "1" }}
                                                    onInput={(e) => {
                                                        e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 2);
                                                    }}
                                                    error={error.priorityText.length > 0}
                                                    helperText={error.priorityText}
                                                />
                                            ) : (
                                                row.priority
                                            )}
                                        </TableCell>
                                        <TableButtons
                                            addDisabled={isAddDisable}
                                            editable={editable}
                                            editableRow={editableRow}
                                            row={row}
                                            updateAddHandler={() => updateAddHandler(row)}
                                            disableEditHandler={() => disableEditHandler()}
                                            enableEditHandler={() => enableEditHandler(row)}
                                            deleteButtonHandler={() => deleteButtonHandler(row)}
                                            detail={false}
                                        />
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            id="be_TablePagination"
                            rowsPerPageOptions={[20, 50, 100]}
                            component="div"
                            count={rows.length}
                            rowsPerPage={rowsPerPage}
                            page={currentPage}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </TableContainer>
                </Grow>
            ) : null}

            <Modal title={t("Delete")} content={t("WillYouDoTransaction")} approved={() => deleteHandler(deletedRow)} isModalOpen={isModalOpen} toggle={toggleModal} />

            <LoadingBackdrop />
        </StyledTableDiv>
    );
}
